import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

import { fetchWorkOrderTable } from "../../../../../../../../../api/kehrblatt/work-orders"
import {
  type CalendarBoardPropertyRef,
  type CalendarBoardWorkOrderRef,
} from "../../../../../../../../../api/kehrblatt/schedule-events"
import WorkOrderCancelReplanOverlay from "../../../../../_shared/_modules/work-order-cancel-replan-overlay"
import { addDays, startOfDay } from "../../../../../../../../../_shared/_logic/date-math"
import { isCancelableAppointment } from "../../../../../_shared/_logic/appointment-eligibility"
import { parseDateLike } from "../../../../../../../_shared/_logic/date-bounds"
import type { EmployeeOption } from "../../../../../../../_shared/_logic/calendar-types"

export type CalendarBulkCommandContext = {
  organizationId: string | null
  loading: boolean
  eventsLoading: boolean
  allowedEmployeeIds?: string[] | null
  employees: EmployeeOption[]
  workOrders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  contactOrders: ContactOrder[]
  refreshEvents: () => Promise<void>
}

const normalizeId = (value: unknown) => `${value ?? ""}`.trim()

const dedupeById = <T extends { id?: string | null }>(values: T[]) => {
  const byId = new Map<string, T>()
  for (const value of values) {
    const id = normalizeId(value.id)

    if (id) byId.set(id, value)
  }

  return [...byId.values()]
}

const asOverlayWorkOrders = (workOrders: CalendarBoardWorkOrderRef[]): WorkOrder[] =>
  workOrders as unknown as WorkOrder[]

const asOverlayProperties = (properties: CalendarBoardPropertyRef[]): Property[] =>
  properties as unknown as Property[]

const loadCancelReplanContext = async (
  context: CalendarBulkCommandContext,
  events: ScheduleEvent[],
) => {
  const organizationId = context.organizationId
  const workOrderIds = dedupeById(
    events.map((event) => ({ id: normalizeId(event.work_order_id) })).filter((entry) => entry.id),
  ).map((entry) => entry.id as string)

  if (!organizationId || !workOrderIds.length) {
    return {
      workOrders: asOverlayWorkOrders(context.workOrders),
      properties: asOverlayProperties(context.properties),
      customers: context.customers,
      contactPoints: context.contactPoints,
      contactOrders: context.contactOrders,
    }
  }

  const projection = await fetchWorkOrderTable({
    organizationId,
    workOrderIds,
    limit: workOrderIds.length,
  }).catch((error) => {
    console.warn("calendar_cancel_replan_context_load_failed", error)

    return null
  })

  if (!projection) {
    return {
      workOrders: asOverlayWorkOrders(context.workOrders),
      properties: asOverlayProperties(context.properties),
      customers: context.customers,
      contactPoints: context.contactPoints,
      contactOrders: context.contactOrders,
    }
  }

  return {
    workOrders: projection.items ?? [],
    properties: projection.properties ?? [],
    customers: projection.customers ?? [],
    contactPoints: projection.contact_points ?? [],
    contactOrders: dedupeById([
      ...(context.contactOrders ?? []),
      ...(projection.contact_orders ?? []),
    ]),
  }
}

export const startCancelReplanForEvents = (
  context: CalendarBulkCommandContext,
  items: ScheduleEvent[],
  emptyMessage: string,
) => {
  void startCancelReplanForEventsAsync(context, items, emptyMessage)
}

const startCancelReplanForEventsAsync = async (
  context: CalendarBulkCommandContext,
  items: ScheduleEvent[],
  emptyMessage: string,
) => {
  const organizationId = context.organizationId

  if (!organizationId) return

  if (context.loading || context.eventsLoading) {
    newNotification({ class: "info", html: "Kalender wird noch geladen. Bitte kurz warten." })

    return
  }

  const cancelableEvents = items.filter(isCancelableAppointment)

  if (!cancelableEvents.length) {
    newNotification({ class: "info", html: emptyMessage })

    return
  }

  const overlayContext = await loadCancelReplanContext(context, cancelableEvents)
  openOverlay({
    title: "Termine verschieben",
    content: WorkOrderCancelReplanOverlay,
    bodyPadding: "none",
    closeOnParentOpen: true,
    isDark: true,
    properties: {
      organizationId,
      mode: "rescheduleAppointments",
      events: cancelableEvents,
      employees: context.employees,
      workOrders: overlayContext.workOrders,
      properties: overlayContext.properties,
      customers: overlayContext.customers,
      contactPoints: overlayContext.contactPoints,
      contactOrders: overlayContext.contactOrders,
      onCompleted: context.refreshEvents,
    },
  })
}

export const startCancelReplanDay = (
  context: CalendarBulkCommandContext,
  {
    day,
    employeeId,
    eligibleEvents,
  }: {
    day: Date
    employeeId?: string | null
    eligibleEvents: ScheduleEvent[]
  },
) => {
  if (context.allowedEmployeeIds != null) {
    const scopedEmployeeId = employeeId ?? null

    if (!scopedEmployeeId || !context.allowedEmployeeIds.includes(scopedEmployeeId)) {
      newNotification({ class: "error", html: "Dieser Mitarbeiterbereich ist schreibgeschützt." })

      return
    }
  }

  const dayStart = startOfDay(day)
  const dayEnd = addDays(dayStart, 1)
  const employeeKey = employeeId || "__unassigned__"
  const columnEvents = eligibleEvents.filter((event) => {
    const start = parseDateLike(event.start_at)
    const end = parseDateLike(event.end_at)

    if (!start || !end || end <= start) return false

    if ((event.employee_id || "__unassigned__") !== employeeKey) return false

    return start < dayEnd && end > dayStart
  })
  startCancelReplanForEvents(
    context,
    columnEvents,
    "In dieser Spalte gibt es keine Termine, die verschoben werden können.",
  )
}
